import { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  ButtonUI,
  FieldEdition,
  LoadingSkeleton,
  showNotification,
  Tooltip,
  TooltipInteractionEnum
} from 'sonata-react-components';

import { HoverTooltip } from '@/components/common/HoverTooltip/HoverTooltip';
import ShareReport from '@/components/common/ShareReport/ShareReport';
import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import { useDoohReportSettings } from '@/components/doohReport/common/hooks/useDoohReportSettings';
import { isSharedReportSession } from '@/helpers/shareReport/shareReport.helper';
import { updateDoohReportName } from '@/services/doohReport.service';
import { useGlobalStore } from '@/store/global';
import { getSession } from '@/store/global/session/selectors';

import type { ReactElement } from 'react';
import type { DoohReportLineItem } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

import './reportHeader.scss';

type Props = {
  lineItem: DoohReportLineItem;
};

function formatDate(dateStr: string | null): { monthDay: string; year: string } | null {
  if (!dateStr) {
    return null;
  }
  const formattedDate = new Date(dateStr).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  const [monthDay, year] = formattedDate.split(',').map(part => part.trim());

  return { monthDay, year };
}

/**
 * DOOH report header, mirroring the driveToStore header.
 *
 * The report title is the name the report was given (editable outside shared report sessions,
 * shared by everyone who opens the report) or the default one.
 */
export default function ReportHeader({ lineItem }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { formatMoney } = useDoohReportFormatters();
  const session = useGlobalStore(getSession);
  const navigate = useNavigate();
  const backButtonId = useId();
  const downloadButtonId = useId();
  const allReportsButtonId = useId();
  const menuButtonId = useId();
  const nameEditionId = useId();
  const { isLoading: isSettingsLoading, settings, updateSettings } = useDoohReportSettings(lineItem.id);
  const [isSavingName, setIsSavingName] = useState(false);

  const startDate = formatDate(lineItem.startDate);
  const endDate = formatDate(lineItem.endDate);
  const isSharedSession = isSharedReportSession(session);

  const withTooltip = (content: string, children: ReactElement) => (
    <Tooltip
      config={{
        component: <span>{content}</span>,
        interactions: [TooltipInteractionEnum.hover],
        placement: 'bottom',
        portal: true,
        strategy: 'fixed'
      }}
    >
      <span>{children}</span>
    </Tooltip>
  );

  const displayName =
    settings?.name ?? formatMessage({ defaultMessage: 'prDOOH Report', id: 'doohReport.header.title' });

  const onNameChange = async (newName: string) => {
    setIsSavingName(true);
    try {
      const result = await updateDoohReportName(lineItem.id, newName);
      updateSettings({ name: result.name });
    } catch (_error) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: "The report name couldn't be updated, please try again",
          id: 'doohReport.header.name.updateError'
        })
      );
    }
    setIsSavingName(false);
  };

  const renderTitle = () => {
    if (isSettingsLoading) {
      return <LoadingSkeleton height={20} width={160} />;
    }
    if (isSharedSession) {
      return (
        <HoverTooltip content={displayName}>
          <span className="reportHeader__leftSectionTitle">{displayName}</span>
        </HoverTooltip>
      );
    }
    return (
      <FieldEdition
        className="reportHeader__nameEdition"
        disabled={isSavingName}
        id={nameEditionId}
        onChange={onNameChange}
        title={displayName}
        value={displayName}
      />
    );
  };

  const notAvailableYet = formatMessage({
    defaultMessage: 'Not available yet',
    id: 'doohReport.header.notAvailableYet'
  });

  return (
    <div className="reportHeader">
      <aside className="reportHeader__leftSection">
        {!isSharedSession && (
          <ButtonUI
            className="subHeader__backButton"
            icon="fal fa-arrow-left"
            iconPosition="left"
            id={backButtonId}
            onClick={() => navigate(-1)}
            shape="squareRounded"
            size="small"
            type="secondary"
          />
        )}
        {renderTitle()}
      </aside>
      <div className="reportHeader__centerSection">
        <div className="reportHeader__entityDetailSection">
          <span className="reportHeader__entityDetailSectionLabel">
            {formatMessage({ defaultMessage: 'Line Item -', id: 'doohReport.header.lineItemLabel' })}
          </span>
          <HoverTooltip content={lineItem.name}>
            <span className="reportHeader__entityDetailName">{lineItem.name}</span>
          </HoverTooltip>
        </div>
        <div className="reportHeader__entityDetailSection">
          <span className="reportHeader__entityDetailSectionLabel">
            {formatMessage({ defaultMessage: 'Budget -', id: 'doohReport.header.budgetLabel' })}
          </span>
          <span className="reportHeader__entityDetailBudget">{formatMoney(lineItem.budget)}</span>
        </div>
        <div className="reportHeader__entityDetailSection">
          <span className="reportHeader__entityDetailSectionLabel">
            {formatMessage({ defaultMessage: 'Dates -', id: 'doohReport.header.datesLabel' })}
          </span>
          <span className="reportHeader__entityDetailDates">
            <span className="reportHeader__entityDetailDatesDate">
              <b>{startDate?.monthDay ?? '—'}</b>
              {startDate ? `, ${startDate.year} ` : ' '}
            </span>
            <span className="reportHeader__entityDetailDatesTo">
              {formatMessage({ defaultMessage: 'to', id: 'doohReport.header.to' })}
            </span>
            <span className="reportHeader__entityDetailDatesDate">
              <b>{endDate?.monthDay ?? '—'}</b>
              {endDate ? `, ${endDate.year}` : ''}
            </span>
          </span>
        </div>
      </div>
      <div className="reportHeader__rightSection">
        <div className="reportHeader__actions">
          {withTooltip(
            notAvailableYet,
            <ButtonUI
              className="reportHeader__actionsDownload"
              disabled
              icon="fas fa-download"
              iconPosition="right"
              id={downloadButtonId}
              shape="squareRounded"
              size="small"
              text={formatMessage({
                defaultMessage: 'Download Report',
                id: 'doohReport.header.downloadReport'
              })}
              type="primary"
            />
          )}
          {withTooltip(
            notAvailableYet,
            <ButtonUI
              disabled
              icon="fas fa-chart-mixed"
              iconPosition="right"
              id={allReportsButtonId}
              shape="squareRounded"
              size="small"
              text={formatMessage({ defaultMessage: 'All reports', id: 'doohReport.header.allReports' })}
              type="tertiary"
            />
          )}
          {!isSharedSession && (
            <ShareReport
              buttonProps={{
                icon: 'fas fa-share-nodes',
                iconPosition: 'right',
                shape: 'squareRounded',
                size: 'small',
                text: formatMessage({ defaultMessage: 'Share report', id: 'doohReport.header.shareReport' }),
                type: 'tertiary'
              }}
              lineItemIds={[lineItem.id]}
              showFullAccessOption={false}
              type="singleDooh"
            />
          )}
          {withTooltip(
            notAvailableYet,
            <ButtonUI
              className="reportHeader__menuButton"
              disabled
              icon="fas fa-ellipsis-vertical"
              id={menuButtonId}
              shape="squareRounded"
              size="small"
              type="secondary"
            />
          )}
        </div>
      </div>
    </div>
  );
}
